import { css, keyframes } from '@emotion/core'
import { spacing, measures, colors, font } from 'styles/variables'
import * as MQ from 'styles/media-queries'

const { unit } = measures

// Keyframes for loading animation
const loadingAnimation = keyframes`
  0% {
    width: 0%;
  }
  50% {
    width: 70%;
  }
  100% {
    width: 100%;
  }
`

export default {
  // Onboarding Screen Styles
  onboardingContainer: (backgroundImage) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    min-height: 100vh;
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0;
    margin: 0;
    box-sizing: border-box;
    z-index: 99999;
    background-color: #000000;
    
    /* Ensure proper viewport handling on mobile */
    ${MQ.PHONES} {
      width: 100vw;
      height: 100dvh;
      overflow: hidden;
    }
    
    /* Curved background image */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 75%;
      background-image: url('${backgroundImage}');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      z-index: 1;
      /* Create curved bottom using SVG clipPath */
      clip-path: url(#bottom-curve);
      -webkit-clip-path: url(#bottom-curve);
    }
    
    /* Mobile and tablet specific */
    ${MQ.PHONES} {
      &::before {
        height: 70%;
        background-size: cover;
        background-position: center top;
        /* Use same SVG curve for mobile */
        clip-path: url(#bottom-curve);
        -webkit-clip-path: url(#bottom-curve);
      }
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      &::before {
        height: 68%;
        background-position: center top;
      }
    }
  `,
  onboardingContent: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    text-align: center;
    z-index: 3;
    width: 100%;
    height: 100%;
    padding: ${spacing(3)};
    padding-bottom: ${spacing(28)};
    position: relative;
    transform: translateY(${spacing(-12)});
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      padding: ${spacing(2.5)};
      padding-bottom: ${spacing(24)};
      transform: translateY(${spacing(-9)});
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      padding: ${spacing(2)} !important;
      padding-bottom: ${spacing(24)} !important;
      transform: translateY(${spacing(-6)}) !important;
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      padding: ${spacing(1.5)};
      padding-bottom: ${spacing(16)};
      transform: translateY(${spacing(-4)});
    }
    
    /* Very small devices - iPhone SE and similar */
    @media (max-width: 375px) and (max-height: 667px) {
      padding-bottom: ${spacing(12)};
      transform: translateY(${spacing(-3)});
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series */
    @media (min-width: 390px) and (max-width: 480px) {
      padding: ${spacing(2)} !important;
      padding-bottom: ${spacing(20)} !important;
      transform: translateY(${spacing(-9)}) !important;
    }
    
    /* Large mobile phones (481px-767px) - ensure all phones get mobile styling */
    @media (min-width: 481px) and (max-width: 767px) {
      padding: ${spacing(2)};
      padding-bottom: ${spacing(24)};
      transform: translateY(${spacing(-6)});
    }
    
    /* Extra small devices */
    @media (max-width: 320px) {
      padding: ${spacing(1)};
      padding-bottom: ${spacing(8)};
      transform: translateY(${spacing(-2)});
    }
  `,
  backArrow: css`
    position: absolute;
    top: ${spacing(4)};
    left: ${spacing(4)};
    z-index: 10;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${spacing(1.5)};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: translateX(-2px);
    }
    
    &:active {
      transform: translateX(0);
    }
    
    svg {
      display: block;
    }
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      top: ${spacing(3.5)};
      left: ${spacing(3.5)};
      padding: ${spacing(1.2)};
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      top: ${spacing(3)};
      left: ${spacing(3)};
      padding: ${spacing(1)};
      
      svg {
        width: 20px;
        height: 20px;
      }
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      top: ${spacing(2.5)};
      left: ${spacing(2.5)};
      padding: ${spacing(0.8)};
      
      svg {
        width: 18px;
        height: 18px;
      }
    }
  `,
  onboardingTitle: css`
    color: ${colors.white100};
    font-size: ${unit * 5.76}px;
    font-weight: ${font.weight.bold};
    font-style: italic;
    font-family: ${font.family};
    margin: 0;
    margin-bottom: ${spacing(2)};
    letter-spacing: -0.5px;
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      font-size: ${unit * 5.2}px;
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      font-size: ${unit * 4.5}px;
      margin-bottom: ${spacing(1.5)};
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      font-size: ${unit * 4}px;
      margin-bottom: ${spacing(1)};
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series - override smartphones */
    @media (min-width: 390px) and (max-width: 480px) {
      font-size: ${unit * 4.2}px !important;
      margin-bottom: ${spacing(1.5)} !important;
      transform: translateY(${spacing(2)}) !important;
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      font-size: ${unit * 3.5}px;
    }
  `,
  titleWithLogo: css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing(2)};
    margin-bottom: ${spacing(2)};
    
    h1 {
      margin-bottom: 0 !important;
    }
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      gap: ${spacing(1.8)};
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      gap: ${spacing(1.5)};
      margin-bottom: ${spacing(1.5)};
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      gap: ${spacing(1.2)};
      margin-bottom: ${spacing(1)};
    }
  `,
  titleLogo: css`
    width: ${unit * 11.25}px;
    height: ${unit * 11.25}px;
    object-fit: contain;
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      width: ${unit * 10.5}px;
      height: ${unit * 10.5}px;
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      width: ${unit * 9}px;
      height: ${unit * 9}px;
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      width: ${unit * 8}px;
      height: ${unit * 8}px;
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series - override smartphones */
    @media (min-width: 390px) and (max-width: 480px) {
      width: ${unit * 8.5}px !important;
      height: ${unit * 8.5}px !important;
      transform: translateY(${spacing(2)}) !important;
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      width: ${unit * 7}px;
      height: ${unit * 7}px;
    }
  `,
  onboardingSubtitle: css`
    color: rgba(255, 255, 255, 0.6);
    font-size: ${unit * 2.88}px;
    font-weight: ${font.weight.normal};
    font-family: ${font.family};
    margin: 0;
    margin-bottom: ${spacing(16)};
    text-align: center;
    line-height: 1.5;
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      font-size: ${unit * 2.7}px;
      margin-bottom: ${spacing(14)};
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      font-size: ${unit * 2.4}px;
      margin-bottom: ${spacing(10)};
      line-height: 1.4;
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      font-size: ${unit * 2.2}px;
      margin-bottom: ${spacing(8)};
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series - override smartphones */
    @media (min-width: 390px) and (max-width: 480px) {
      font-size: ${unit * 2.5}px !important;
      margin-bottom: ${spacing(10)} !important;
      transform: translateY(${spacing(2)}) !important;
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      font-size: ${unit * 2}px;
      margin-bottom: ${spacing(6)};
    }
  `,
  thirdPageTitleContainer: css`
    transform: translateY(${spacing(-30)});
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      transform: translateY(${spacing(-26)});
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      transform: translateY(${spacing(-20)});
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      transform: translateY(${spacing(-14)});
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series */
    @media (min-width: 390px) and (max-width: 480px) {
      transform: translateY(${spacing(-8)}) !important;
    }
  `,
  thirdPageTextDown: css`
    transform: translateY(${spacing(-30)});
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      transform: translateY(${spacing(-26)});
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      transform: translateY(${spacing(-20)});
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      transform: translateY(${spacing(-14)});
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series */
    @media (min-width: 390px) and (max-width: 480px) {
      transform: translateY(${spacing(-8)}) !important;
    }
  `,
  thirdPageSubtitleSpacing: css`
    margin-bottom: ${spacing(4)} !important;
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      margin-bottom: ${spacing(3.5)} !important;
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      margin-bottom: ${spacing(2.5)} !important;
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      margin-bottom: ${spacing(2)} !important;
    }
  `,
  firstAndSecondPageContent: css`
    padding-bottom: ${spacing(16)} !important;
    transform: translateY(${spacing(-20)});
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      padding-bottom: ${spacing(14)} !important;
      transform: translateY(${spacing(-16)});
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      padding-bottom: ${spacing(16)} !important;
      transform: translateY(${spacing(-15)}) !important;
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      padding-bottom: ${spacing(10)} !important;
      transform: translateY(${spacing(-8)});
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series */
    @media (min-width: 390px) and (max-width: 480px) {
      padding-bottom: ${spacing(18)} !important;
      transform: translateY(${spacing(-20)}) !important;
    }
    
    /* Large mobile phones (481px-767px) - ensure all phones get mobile styling */
    @media (min-width: 481px) and (max-width: 767px) {
      padding-bottom: ${spacing(16)} !important;
      transform: translateY(${spacing(-15)}) !important;
    }
    
    /* iPhone SE and similar small devices */
    @media (max-width: 375px) and (max-height: 667px) {
      padding-bottom: ${spacing(6)} !important;
      transform: translateY(${spacing(-6)});
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      padding-bottom: ${spacing(5)} !important;
      transform: translateY(${spacing(-5)});
    }
  `,
  thirdPageContent: css`
    padding-bottom: ${spacing(16)} !important;
    transform: translateY(${spacing(-35)});
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      padding-bottom: ${spacing(14)} !important;
      transform: translateY(${spacing(-30)});
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      padding-bottom: ${spacing(12)} !important;
      transform: translateY(${spacing(-24)});
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      padding-bottom: ${spacing(10)} !important;
      transform: translateY(${spacing(-16)});
    }
    
    /* iPhone SE and similar small devices */
    @media (max-width: 375px) and (max-height: 667px) {
      padding-bottom: ${spacing(6)} !important;
      transform: translateY(${spacing(-14)});
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      padding-bottom: ${spacing(5)} !important;
      transform: translateY(${spacing(-12)});
    }
  `,
  paginationDots: css`
    display: flex;
    justify-content: center;
    gap: ${spacing(1.5)};
    margin-bottom: ${spacing(4)};
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      margin-bottom: ${spacing(3.5)};
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      margin-bottom: ${spacing(2.5)};
      gap: ${spacing(1.2)};
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      margin-bottom: ${spacing(2)};
      gap: ${spacing(1)};
    }
  `,
  dot: css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.25);
    transition: background-color 0.3s ease;
    cursor: pointer;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.4);
    }
  `,
  activeDot: css`
    background-color: #5AC98A;
  `,
  buttonContainer: css`
    display: flex;
    justify-content: center;
    gap: ${spacing(2)};
    width: 100%;
    margin-top: ${spacing(1)};
    margin-bottom: ${spacing(1)};
    transform: translateY(${spacing(-14)});
    
    /* Make buttons narrower when they appear together (third page only) */
    button {
      width: 40% !important;
      max-width: 150px !important;
      padding: ${spacing(2.2, 0)} !important;
    }
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      gap: ${spacing(1.8)};
      transform: translateY(${spacing(-12)});
      
      button {
        width: 42% !important;
        max-width: 140px !important;
        padding: ${spacing(2.1, 0)} !important;
      }
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      margin-top: ${spacing(0.5)};
      margin-bottom: ${spacing(1)};
      gap: ${spacing(1.5)};
      transform: translateY(${spacing(-9)});
      
      button {
        width: 44% !important;
        max-width: 130px !important;
        padding: ${spacing(1.8, 0)} !important;
      }
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      gap: ${spacing(1.2)};
      transform: translateY(${spacing(-8)});
      
      button {
        width: 46% !important;
        max-width: 120px !important;
        padding: ${spacing(1.6, 0)} !important;
      }
    }
    
    /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series */
    @media (min-width: 390px) and (max-width: 480px) {
      transform: translateY(${spacing(-2)}) !important;
    }
    
    /* Large mobile phones (481px-767px) */
    @media (min-width: 481px) and (max-width: 767px) {
      transform: translateY(${spacing(-9)});
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      gap: ${spacing(1)};
      transform: translateY(${spacing(-7)});
      
      button {
        width: 48% !important;
        max-width: 110px !important;
        padding: ${spacing(1.4, 0)} !important;
      }
    }
  `,

  signupButton: css`
    background: #5AC98A;
    color: #000000;
    border: none;
    border-radius: ${unit * 4}px;
    padding: ${spacing(2.6, 0)};
    font-size: ${unit * 2.6}px;
    font-weight: ${font.weight.bold};
    font-family: ${font.family};
    cursor: pointer;
    transition: all 0.3s ease;
    width: 85%;
    max-width: 280px;
    
    /* Single button styling */
    &:only-child {
      margin: ${spacing(4)} auto ${spacing(2)} auto;
      transform: translateY(${spacing(-10)});
      
      /* Tablet specific */
      ${MQ.TABLETS} {
        margin: ${spacing(3.5)} auto ${spacing(1.8)} auto;
        transform: translateY(${spacing(-8)});
      }
      
      /* Mobile specific */
      ${MQ.PHONES} {
        margin: ${spacing(2.5)} auto ${spacing(1.2)} auto !important;
        transform: translateY(${spacing(-12)}) !important;
        width: 44% !important;
        max-width: 130px !important;
        padding: ${spacing(1.8, 0)} !important;
      }
      
      /* Small mobile devices */
      ${MQ.SMARTPHONES} {
        margin: ${spacing(2)} auto ${spacing(1)} auto;
        transform: translateY(${spacing(-4)});
        width: 46% !important;
        max-width: 120px !important;
        padding: ${spacing(1.6, 0)} !important;
      }
      
      /* Mid-size mobile phones (390px-480px) - iPhone 12/13/14 series */
      @media (min-width: 390px) and (max-width: 480px) {
        margin: ${spacing(2.5)} auto ${spacing(1.2)} auto !important;
        transform: translateY(${spacing(-4)}) !important;
        width: 46% !important;
        max-width: 125px !important;
        padding: ${spacing(1.7, 0)} !important;
        font-size: ${unit * 2.2}px !important;
      }
      
      /* Large mobile phones (481px-767px) - ensure all phones get mobile styling */
      @media (min-width: 481px) and (max-width: 767px) {
        margin: ${spacing(2.5)} auto ${spacing(1.2)} auto !important;
        transform: translateY(${spacing(-12)}) !important;
        width: 44% !important;
        max-width: 130px !important;
        padding: ${spacing(1.8, 0)} !important;
      }
      
      /* Very small devices */
      @media (max-width: 320px) {
        width: 48% !important;
        max-width: 110px !important;
        padding: ${spacing(1.4, 0)} !important;
      }
    }
    
    &:hover {
      background: #4BB079;
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      font-size: ${unit * 2.5}px;
      padding: ${spacing(2.5, 0)};
      width: 87%;
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      font-size: ${unit * 2.3}px;
      padding: ${spacing(2.2, 0)};
      width: 90%;
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      font-size: ${unit * 2.1}px;
      padding: ${spacing(2, 0)};
      width: 92%;
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      font-size: ${unit * 1.9}px;
      padding: ${spacing(1.8, 0)};
      width: 95%;
    }
  `,
  loginButton: css`
    background: transparent;
    color: #5AC98A;
    border: 2px solid #5AC98A;
    border-radius: ${unit * 4}px;
    padding: ${spacing(2.4, 0)};
    font-size: ${unit * 2.6}px;
    font-weight: ${font.weight.bold};
    font-family: ${font.family};
    cursor: pointer;
    transition: all 0.3s ease;
    width: 85%;
    max-width: 280px;
    
    &:hover {
      background: rgba(90, 201, 138, 0.1);
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    /* Tablet specific */
    ${MQ.TABLETS} {
      font-size: ${unit * 2.5}px;
      padding: ${spacing(2.3, 0)};
      width: 87%;
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      font-size: ${unit * 2.3}px;
      padding: ${spacing(2, 0)};
      width: 90%;
    }
    
    /* Small mobile devices */
    ${MQ.SMARTPHONES} {
      font-size: ${unit * 2.1}px;
      padding: ${spacing(1.8, 0)};
      width: 92%;
    }
    
    /* Very small devices */
    @media (max-width: 320px) {
      font-size: ${unit * 1.9}px;
      padding: ${spacing(1.6, 0)};
      width: 95%;
    }
  `,
  swipeIndicator: css`
    display: none;
  `,
  thirdPageSwipeIndicator: css`
    display: none;
  `,

  // Splash Screen Styles
  splashContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile */
    background-color: #000000;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: ${spacing(2)};
    box-sizing: border-box;
    z-index: 9999;
    
    /* Ensure proper viewport handling on all mobile devices */
    ${MQ.TABLETS} {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100dvh;
      padding-top: 0;
      margin-top: 0;
      overflow: hidden;
    }
    
    /* Mobile specific */
    ${MQ.PHONES} {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100dvh;
      padding-top: 0;
      margin-top: 0;
      overflow: hidden;
    }
  `,
  splashContent: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
  `,
  logoContainer: css`
    margin-bottom: ${spacing(6)};
  `,
  logoBorder: css`
    width: ${unit * 20}px;
    height: ${unit * 20}px;
    max-width: 280px;
    border-radius: ${unit * 6}px;
    background-color: #000000;
    padding: 0px;
    border: 1px solid rgba(255, 255, 255, 0.8);
    box-shadow: inset 0 0 0 8px rgba(255, 255, 255, 0.8);
    filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.3));
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  logo: css`
    width: 100%;
    height: auto;
    object-fit: contain;
    transform: scale(1.1);
  `,
  title: css`
    color: ${colors.white100};
    font-size: ${unit * 5.0625}px;
    font-weight: ${font.weight.bold};
    font-style: italic;
    font-family: ${font.family};
    margin: 0;
    margin-bottom: ${spacing(1.5)};
    letter-spacing: -0.5px;
  `,
  subtitle: css`
    color: ${colors.white60};
    font-size: ${unit * 2.475}px;
    font-weight: ${font.weight.normal};
    font-family: ${font.family};
    margin: 0;
    text-align: center;
    line-height: 1.4;
  `,
  loadingContainer: css`
    display: none;
  `,
  loadingBar: css`
    display: none;
  `,

  // Desktop AboutUs Styles
  container: css`
    display: flex;
    flex-direction: column;
    padding: ${spacing(5, 5, 0, 5)};
    gap: ${unit * 5}px;
  `,
  top: css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    border-radius: ${unit * 2}px;
    padding: ${spacing(6.5, 0)};
    background-color: ${colors.black80};
    overflow: hidden;
    font-family: ${font.family};

    *:not(img) {
      z-index: 2;
    }
  `,
  topImage: css`
    position: absolute;
    max-height: 120%;
    filter: opacity(0.5);
  `,
  topTitle: css`
    margin-bottom: ${spacing(1.5)};
    color: ${colors.white100};
    font-size: ${font.size24};
    font-style: italic;
    font-weight: ${font.weight.extraBold};
    font-family: ${font.family};
  `,
  description: css`
    color: ${colors.white60};
    font-size: ${font.size18};
    text-align: center;
    line-height: 24px;
  `,
  logInText: css`
    color: ${colors.white100};
  `,
  logInLink: css`
    text-decoration: none;
    color: ${colors.purples.purple1};
    cursor: pointer;
  `,
  signUpBtn: css`
    margin-top: ${spacing(5)};
    margin-bottom: ${spacing(2.5)};
    background-color: ${colors.purples.purple1};
    color: ${colors.white100};
    width: ${unit * 30}px;
  `,
  links: css`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: ${spacing(1.25)};
    width: 100%;
    box-sizing: border-box;
  `,
  link: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-decoration: none;
    width: 100%;
    padding: ${spacing(2.5)};
    background-color: ${colors.grays.gray8};
    color: ${colors.white100};
    border-radius: ${unit * 1.5}px;
    box-sizing: border-box;
    transition: all 0.3s ease;
    font-family: ${font.family};
    :hover {
      background-color: ${colors.grays.gray7};
    }
  `,
}
